import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { MantineProvider, createTheme } from '@mantine/core'
import { DatesProvider } from '@mantine/dates'
import { Notifications } from '@mantine/notifications'
import '@mantine/core/styles.css'
import '@mantine/dates/styles.css'
import '@mantine/notifications/styles.css'
import './index.css'
import App from './App'
import 'dayjs/locale/es'

const theme = createTheme({
  primaryColor: 'indigo',
  primaryShade: 6,
  defaultRadius: 'lg',
  defaultGradient: { from: 'indigo', to: 'cyan', deg: 135 },
  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  headings: {
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    sizes: {
      h1: { fontSize: '2.35rem', fontWeight: 900, lineHeight: '1.02' },
      h2: { fontSize: '1.95rem', fontWeight: 800, lineHeight: '1.05' },
      h3: { fontSize: '1.55rem', fontWeight: 700, lineHeight: '1.1' },
      h4: { fontSize: '1.2rem', fontWeight: 700, lineHeight: '1.12' },
    }
  },
  breakpoints: {
    xs: '36em',
    sm: '48em',
    md: '62em',
    lg: '75em',
    xl: '88em',
    xxl: '120em',
  },
  colors: {
    slate: ['#f8fafc', '#f1f5f9', '#e2e8f0', '#cbd5e1', '#94a3b8', '#64748b', '#475569', '#334155', '#1e293b', '#0f172a'],
  },
  shadows: {
    xs: '0 10px 24px rgba(15, 23, 42, 0.05)',
    sm: '0 14px 34px rgba(15, 23, 42, 0.07)',
    md: '0 18px 48px rgba(15, 23, 42, 0.10)',
    lg: '0 24px 64px rgba(15, 23, 42, 0.12)',
    xl: '0 32px 88px rgba(15, 23, 42, 0.16)',
  },
  components: {
    Button: {
      defaultProps: {
        radius: 'xl',
        size: 'md',
      },
      styles: {
        root: {
          fontWeight: 700,
          letterSpacing: '-0.015em',
          boxShadow: '0 10px 24px rgba(15, 23, 42, 0.08)',
          transition: 'transform 160ms ease, box-shadow 160ms ease, background-color 160ms ease, border-color 160ms ease',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 16px 28px rgba(15, 23, 42, 0.10)',
          },
        },
      },
    },
    Select: {
      defaultProps: {
        radius: 'lg',
        size: 'md',
      },
    },
    NumberInput: {
      defaultProps: {
        radius: 'lg',
        size: 'md',
      },
    },
    Textarea: {
      defaultProps: {
        radius: 'lg',
        size: 'md',
      },
    },
    Card: {
      defaultProps: {
        radius: 'lg',
        p: 'lg',
      },
      styles: {
        root: {
          background: 'linear-gradient(180deg, rgba(255,255,255,0.96), rgba(248,250,252,0.92))',
          borderColor: 'rgba(226, 232, 240, 0.95)',
          boxShadow: '0 16px 36px rgba(15, 23, 42, 0.06)',
        },
      },
    },
    Paper: {
      defaultProps: {
        radius: 'lg',
        p: 'lg',
        shadow: 'sm',
        withBorder: true,
      },
      styles: {
        root: {
          background: 'linear-gradient(180deg, rgba(255,255,255,0.94), rgba(248,250,252,0.88))',
          borderColor: 'rgba(226, 232, 240, 0.95)',
        },
      },
    },
    Alert: {
      defaultProps: {
        radius: 'lg',
        variant: 'light',
      },
    },
    Modal: {
      defaultProps: {
        radius: 'lg',
        padding: 'xl',
        centered: true,
      },
      styles: {
        content: {
          backdropFilter: 'blur(14px)',
        },
      },
    },
    Container: {
      defaultProps: {
        size: 'xl',
        px: 'md',
      },
    },
    TextInput: {
      defaultProps: {
        radius: 'lg',
        size: 'md',
      },
    },
    PasswordInput: {
      defaultProps: {
        radius: 'lg',
        size: 'md',
      },
    },
    Table: {
      defaultProps: {
        striped: true,
        highlightOnHover: true,
        withBorder: true,
        withColumnBorders: true,
        horizontalSpacing: 'lg',
        verticalSpacing: 'md',
        fontSize: 'sm',
      },
      styles: {
        table: {
          overflow: 'hidden',
          borderRadius: '1rem',
        },
        th: {
          backgroundColor: '#f8fafc',
          color: '#475569',
          fontWeight: 700,
          borderBottom: '1px solid rgba(226, 232, 240, 0.95)',
        },
        td: {
          borderColor: 'rgba(226, 232, 240, 0.82)',
        }
      },
    },
    Badge: {
      defaultProps: {
        radius: 'xl',
        variant: 'light',
      },
      styles: {
        root: {
          fontWeight: 700,
          letterSpacing: '-0.01em',
        },
      },
    },
    Progress: {
      defaultProps: {
        radius: 'xl',
        size: 'lg',
      },
    },
    Tabs: {
      defaultProps: {
        radius: 'xl',
      },
    },
    Checkbox: {
      defaultProps: {
        radius: 'md',
      },
    },
    Switch: {
      defaultProps: {
        radius: 'xl',
      },
    },
  },
  other: {
    fontWeightBold: 700,
    borderRadius: '1rem',
    transitionDuration: '200ms',
  },
});

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <MantineProvider theme={theme}>
      <Notifications />
      <DatesProvider settings={{ locale: 'es', firstDayOfWeek: 1 }}>
        <App />
      </DatesProvider>
    </MantineProvider>
  </StrictMode>,
)
